/*
 * Назначение: мастер создания нового проекта.
 * Функционал: 5 шагов с прогресс-баром, валидация, локальное состояние,
 * запрос POST /api/admin/projects на финальном шаге, возможность сидинга
 * демо-БЗ. После создания проект автоматически становится активным
 * (через ProjectProvider) и пользователь попадает на /dashboard.
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Rocket,
  Send,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { projectsApi } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import type { ProjectCreatePayload, Tone } from "../../lib/types";
import { useProject } from "../components/project/project-provider";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

interface WizardState {
  slug: string;
  name: string;
  domain: string;
  description: string;
  tone: Tone;
  greeting_text: string;
  target_actions: string;
  quick_replies: string;
  seed_demo_kb: boolean;
  start_now: boolean;
}

const STEPS = [
  { num: 1, title: "Основное", description: "Имя проекта, домен, тон" },
  { num: 2, title: "Поведение", description: "Приветствие и быстрые кнопки" },
  { num: 3, title: "Целевые действия", description: "К чему вести клиента" },
  { num: 4, title: "База знаний", description: "Стартовый шаблон или с нуля" },
  { num: 5, title: "Готово", description: "Проверка и создание" },
];

const splitLines = (s: string) =>
  s.split("\n").map((x) => x.trim()).filter(Boolean);

const slugify = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[ё]/g, "e")
    .replace(/[а]/g, "a")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

const SLUG_REGEX = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;

function validateStep(step: number, s: WizardState): string | null {
  if (step === 1) {
    if (!s.name.trim()) return "Укажи название проекта";
    if (!s.slug.trim()) return "Укажи код проекта (slug)";
    if (!SLUG_REGEX.test(s.slug)) {
      return "Код может содержать только латинские буквы, цифры и дефис; от 1 символа";
    }
  }
  return null;
}

export function ProjectWizardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrent } = useProject();

  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [autoSlug, setAutoSlug] = useState(true);

  const [form, setForm] = useState<WizardState>({
    slug: "",
    name: "",
    domain: "",
    description: "",
    tone: "friendly",
    greeting_text: "",
    target_actions: "оставить заявку",
    quick_replies: "",
    seed_demo_kb: false,
    start_now: true,
  });

  const setName = (next: string) => {
    setForm((s) => ({
      ...s,
      name: next,
      slug: autoSlug ? slugify(next) : s.slug,
    }));
  };

  const setSlugManually = (next: string) => {
    setAutoSlug(false);
    setForm((s) => ({ ...s, slug: next.toLowerCase() }));
  };

  const next = () => {
    const err = validateStep(step, form);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const prev = () => {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: ProjectCreatePayload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        domain: form.domain.trim() || null,
        description: form.description || null,
        greeting_text: form.greeting_text || null,
        tone: form.tone,
        target_actions: splitLines(form.target_actions),
        quick_replies: splitLines(form.quick_replies),
        seed_demo_kb: form.seed_demo_kb,
      };
      return projectsApi.create(payload);
    },
    onSuccess: (project) => {
      toast.success(`Проект «${project.name}» создан`);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      if (form.start_now) {
        setCurrent(project.slug);
        navigate("/dashboard");
      } else {
        navigate("/");
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError("Проект с таким кодом уже существует. Поменяй slug на шаге 1.");
          setStep(1);
        } else {
          setError(err.detail || "Не удалось создать проект");
        }
      } else {
        setError("Сервер недоступен");
      }
    },
  });

  const progress = useMemo(() => Math.round((step / STEPS.length) * 100), [step]);
  const meta = STEPS[step - 1];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Создать проект</h2>
          <p className="text-sm text-muted-foreground">
            Новый ИИ-продавец под отдельный сайт или направление бизнеса
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate(-1)} disabled={createMutation.isPending}>
          Отмена
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>
            Шаг {step} из {STEPS.length}: {meta.title}
          </span>
          <span>{progress}%</span>
        </div>
        <Progress value={progress} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{meta.title}</CardTitle>
          <CardDescription>{meta.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="w-name">Название проекта</Label>
                <Input
                  id="w-name"
                  placeholder="Например: «Сайт ателье»"
                  value={form.name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="w-slug">
                  Код проекта (slug){" "}
                  <span className="text-muted-foreground text-xs font-normal">
                    латиница, цифры и дефис
                  </span>
                </Label>
                <Input
                  id="w-slug"
                  placeholder="atelier"
                  value={form.slug}
                  onChange={(e) => setSlugManually(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Под этим кодом проект будет принимать сообщения виджета. Поменять
                  потом нельзя — выбирай аккуратно.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="w-domain">Домен сайта (опционально)</Label>
                  <Input
                    id="w-domain"
                    placeholder="example.com"
                    value={form.domain}
                    onChange={(e) => setForm((s) => ({ ...s, domain: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="w-tone">Тон общения</Label>
                  <Select
                    value={form.tone}
                    onValueChange={(v) => setForm((s) => ({ ...s, tone: v as Tone }))}
                  >
                    <SelectTrigger id="w-tone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly">Дружелюбный</SelectItem>
                      <SelectItem value="business">Деловой</SelectItem>
                      <SelectItem value="expert">Экспертный</SelectItem>
                      <SelectItem value="custom">Свой</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="w-desc">Краткое описание (опционально)</Label>
                <Textarea
                  id="w-desc"
                  rows={3}
                  placeholder="Чем компания занимается, для кого работает"
                  value={form.description}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, description: e.target.value }))
                  }
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="w-greet">Приветственное сообщение</Label>
                <Textarea
                  id="w-greet"
                  rows={4}
                  placeholder="Привет! Я ИИ-консультант… Расскажу о наших услугах и помогу оставить заявку."
                  value={form.greeting_text}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, greeting_text: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Сразу после открытия чата клиент видит это сообщение. Можно
                  оставить пустым — тогда ИИ начнёт диалог сам.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="w-quick">
                  Быстрые кнопки (по одной в строке, опционально)
                </Label>
                <Textarea
                  id="w-quick"
                  rows={3}
                  placeholder={"Сделать сайт\nСколько стоит?\nСроки"}
                  value={form.quick_replies}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, quick_replies: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Эти кнопки появятся под приветствием в виджете на сайте.
                </p>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="w-targets">Целевые действия (по одному в строке)</Label>
                <Textarea
                  id="w-targets"
                  rows={5}
                  placeholder={"оставить заявку\nполучить расчёт\nзаписаться на консультацию"}
                  value={form.target_actions}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, target_actions: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Это то, к чему ИИ будет вести клиента. Список можно поменять
                  потом в «Настройках → Общие».
                </p>
              </div>
              <Alert>
                <Sparkles className="h-4 w-4" />
                <AlertDescription>
                  Хотя бы одно действие желательно указать — без целей ИИ
                  ограничится консультацией без явного предложения оставить
                  контакт.
                </AlertDescription>
              </Alert>
            </>
          )}

          {step === 4 && (
            <>
              <div className="flex items-start gap-3 p-4 border rounded-lg">
                <Switch
                  id="w-seed"
                  checked={form.seed_demo_kb}
                  onCheckedChange={(v) =>
                    setForm((s) => ({ ...s, seed_demo_kb: Boolean(v) }))
                  }
                />
                <div className="space-y-1">
                  <Label htmlFor="w-seed">Подсадить шаблонную базу знаний</Label>
                  <p className="text-sm text-muted-foreground">
                    16 элементов из примера агентства сайтов и рекламы — услуги,
                    FAQ, типовые возражения, правила. Удобно как стартовая
                    точка: потом отредактируешь под себя в разделе «База знаний».
                  </p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Стартовать без БЗ тоже можно — добавишь элементы позже
                  вручную или через клонирование другого проекта.
                </AlertDescription>
              </Alert>
            </>
          )}

          {step === 5 && (
            <>
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Название</p>
                    <p className="font-medium">{form.name || "—"}</p>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    {form.slug || "—"}
                  </Badge>
                </div>
                {form.domain && (
                  <div>
                    <p className="text-xs text-muted-foreground">Домен</p>
                    <p>{form.domain}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Тон</p>
                    <p>
                      {{
                        friendly: "Дружелюбный",
                        business: "Деловой",
                        expert: "Экспертный",
                        custom: "Свой",
                      }[form.tone] ?? form.tone}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Стартовая БЗ</p>
                    <p>{form.seed_demo_kb ? "Шаблон агентства (16 элементов)" : "Пустая"}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Целевые действия</p>
                  {splitLines(form.target_actions).length === 0 ? (
                    <p className="text-sm">не заданы</p>
                  ) : (
                    <ul className="text-sm list-disc pl-5">
                      {splitLines(form.target_actions).map((a) => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="w-start"
                  checked={form.start_now}
                  onCheckedChange={(v) =>
                    setForm((s) => ({ ...s, start_now: Boolean(v) }))
                  }
                />
                <Label htmlFor="w-start">Сразу переключиться на новый проект</Label>
              </div>

              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Каналы (Telegram/MAX), модели и менеджеры настроишь после
                  создания в разделе «Настройки» — для них нужны токены и
                  контакты.
                </AlertDescription>
              </Alert>
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={prev} disabled={step === 1 || createMutation.isPending}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Назад
        </Button>

        {step < STEPS.length ? (
          <Button onClick={next}>
            Далее
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="mr-2 h-4 w-4" />
            )}
            Создать проект
          </Button>
        )}
      </div>
    </div>
  );
}
